
import { initializeApp } from 'firebase/app';
import { 
  getFirestore, collection, doc, setDoc, getDoc, 
  updateDoc, onSnapshot, Unsubscribe, deleteDoc 
} from 'firebase/firestore';
import { 
  getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, 
  signOut, onAuthStateChanged 
} from 'firebase/auth';
import { 
  Product, LogisticsPartner, Order, User, UserRole, 
  DeliveryStatus, OrderForm, WebLead, LeadStatus, AbandonedCart, AdsBudget, Widget,
  RestockRecord, WaybillRecord, StateConfig
} from '../types';

const firebaseConfig = {
  apiKey: "AIzaSyDjIST5wP--TJhSxmbDqvgTSHUUFeMJVwE",
  authDomain: "magiracrm.firebaseapp.com",
  projectId: "magiracrm",
  storageBucket: "magiracrm.firebasestorage.app",
  messagingSenderId: "960782141874",
  appId: "1:960782141874:web:c8ba969930b2b5dd0b7cab",
  measurementId: "G-CC1R1BDWTJ"
};

const app = initializeApp(firebaseConfig);
const firestore = getFirestore(app);
const auth = getAuth(app);

type Listener = () => void;

class FirebaseDb {
  private data: {
    products: Product[];
    logistics: LogisticsPartner[];
    orders: Order[];
    forms: OrderForm[];
    leads: WebLead[];
    users: User[];
    abandoned: AbandonedCart[];
    budgets: AdsBudget[];
    widgets: Widget[];
    restocks: RestockRecord[];
    waybills: WaybillRecord[];
    states: StateConfig[];
  } = {
    products: [],
    logistics: [],
    orders: [],
    forms: [],
    leads: [],
    users: [],
    abandoned: [],
    budgets: [],
    widgets: [],
    restocks: [],
    waybills: [],
    states: []
  };

  private currentUser: User | null = null;
  private originalAdminUser: User | null = null;
  private listeners: Set<Listener> = new Set();
  private unsubscribers: Unsubscribe[] = [];
  private authInitialized = false;

  constructor() {
    this.initAuth();
  }

  private initAuth() {
    onAuthStateChanged(auth, async (fbUser) => {
      if (fbUser) {
        try {
          const userRef = doc(firestore, 'users', fbUser.uid);
          const userDoc = await getDoc(userRef);
          
          if (userDoc.exists()) {
            this.currentUser = { id: fbUser.uid, ...userDoc.data() } as User;
            if (this.currentUser.isApproved) {
              this.initRealtimeSync();
            } else {
              this.stopRealtimeSync();
            }
          } else {
            this.currentUser = null;
          }
        } catch (err) {
          this.currentUser = null;
        }
      } else {
        this.currentUser = null;
        this.stopRealtimeSync();
      }
      this.authInitialized = true;
      this.notify();
    });
  }

  private initRealtimeSync() {
    this.stopRealtimeSync();
    if (!this.currentUser || !this.currentUser.isApproved) return;

    const isAdmin = this.currentUser.role === UserRole.ADMIN;
    const collectionsToSync = ['products', 'logistics', 'orders', 'forms', 'leads', 'abandoned_carts', 'ads_budgets', 'widgets', 'restocks', 'waybills', 'states'];
    
    if (isAdmin) {
      collectionsToSync.push('users');
    }

    collectionsToSync.forEach(colName => {
      try {
        let key = colName;
        if (colName === 'abandoned_carts') key = 'abandoned';
        if (colName === 'ads_budgets') key = 'budgets';
        
        const unsub = onSnapshot(
          collection(firestore, colName), 
          (snapshot) => {
            this.data[key as keyof typeof this.data] = snapshot.docs.map(d => ({
              id: d.id,
              ...d.data()
            })) as any;
            this.notify();
          }
        );
        this.unsubscribers.push(unsub);
      } catch (e) {}
    });
  }

  private stopRealtimeSync() {
    this.unsubscribers.forEach(unsub => unsub());
    this.unsubscribers = [];
  }

  subscribe(listener: Listener) {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  private notify() {
    this.listeners.forEach(l => l());
  }

  isAuthReady() { return this.authInitialized; }
  getCurrentUser() { return this.currentUser; }
  getUsers() { return [...this.data.users]; }
  isImpersonating() { return !!this.originalAdminUser; }

  impersonateUser(user: User) {
    if (this.currentUser?.role !== UserRole.ADMIN && !this.originalAdminUser) return;
    if (!this.originalAdminUser) {
      this.originalAdminUser = this.currentUser;
    }
    this.currentUser = user;
    this.initRealtimeSync();
    this.notify();
  }

  stopImpersonation() {
    if (this.originalAdminUser) {
      this.currentUser = this.originalAdminUser;
      this.originalAdminUser = null;
      this.initRealtimeSync();
      this.notify();
    }
  }

  getPendingUserCount() {
    return this.data.users.filter(u => u.status === 'pending').length;
  }

  async register(name: string, email: string, phone: string, password: string, requestedRole: UserRole) {
    const isBootstrapAdmin = email.toLowerCase() === 'admin@magiracrm.store';
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const uid = userCredential.user.uid;
    const finalRole = isBootstrapAdmin ? UserRole.ADMIN : requestedRole;
    const finalStatus = isBootstrapAdmin ? 'approved' : 'pending';

    const userData: Partial<User> = {
      name,
      email,
      phone,
      role: finalRole,
      status: finalStatus as any,
      isApproved: finalStatus === 'approved',
      registeredAt: new Date().toISOString()
    };

    await setDoc(doc(firestore, 'users', uid), userData);
    this.currentUser = { id: uid, ...userData } as User;
    if (this.currentUser.isApproved) {
      this.initRealtimeSync();
    }
    this.notify();
    return this.currentUser;
  }

  async login(email: string, password?: string) {
    if (!password) throw new Error("Password required");
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const userDoc = await getDoc(doc(firestore, 'users', userCredential.user.uid));
    
    if (!userDoc.exists()) throw new Error("Profile not found.");
    const profile = userDoc.data() as User;
    
    if (profile.status === 'pending') throw new Error("Account pending approval");
    if (profile.status === 'rejected') throw new Error("Account access denied");
    
    this.currentUser = { ...profile, id: userCredential.user.uid };
    this.initRealtimeSync();
    this.notify();
    return this.currentUser;
  }

  async logout() {
    this.stopRealtimeSync();
    await signOut(auth);
    this.currentUser = null;
    this.notify();
  }

  async approveUser(userId: string) {
    if (this.currentUser?.role !== UserRole.ADMIN) return;
    await updateDoc(doc(firestore, 'users', userId), { status: 'approved', isApproved: true });
  }

  async rejectUser(userId: string) {
    if (this.currentUser?.role !== UserRole.ADMIN) return;
    await updateDoc(doc(firestore, 'users', userId), { status: 'rejected', isApproved: false });
  }

  async switchUserRole(newRole: UserRole) {
    if (this.currentUser?.role === UserRole.ADMIN) {
      this.currentUser.role = newRole;
      this.notify();
      return this.currentUser;
    }
    return null;
  }

  getProducts() { return [...this.data.products]; }
  getOrders() { return [...this.data.orders]; }
  getLogistics() { return [...this.data.logistics]; }
  getForms() { return [...this.data.forms]; }
  getLeads() { return [...this.data.leads]; }
  getAbandonedCarts() { return [...this.data.abandoned]; }
  getBudgets() { return [...this.data.budgets]; }
  getWidgets() { return [...this.data.widgets]; }
  getRestocks() { return [...this.data.restocks]; }
  getWaybills() { return [...this.data.waybills]; }
  getStates() { return [...this.data.states]; }

  async saveProduct(product: Product) {
    const { id, ...rest } = product;
    await setDoc(doc(firestore, 'products', id), rest);
  }
  async createOrder(order: Order) {
    const { id, ...rest } = order;
    await setDoc(doc(firestore, 'orders', id), rest);
  }
  async updateOrderStatus(orderId: string, status: DeliveryStatus, extra?: any) {
    const updates: any = { deliveryStatus: status };
    if (extra?.logisticsCost !== undefined) updates.logisticsCost = extra.logisticsCost;
    if (extra?.rescheduleDate) updates.rescheduleDate = extra.rescheduleDate;
    if (extra?.rescheduleNotes) updates.rescheduleNotes = extra.rescheduleNotes;
    if (extra?.reminderEnabled !== undefined) updates.reminderEnabled = extra.reminderEnabled;
    await updateDoc(doc(firestore, 'orders', orderId), updates);
  }
  async saveLogistics(partner: LogisticsPartner) {
    const { id, ...rest } = partner;
    await setDoc(doc(firestore, 'logistics', id), rest);
  }
  async saveForm(form: OrderForm) {
    const { id, ...rest } = form;
    await setDoc(doc(firestore, 'forms', id), rest);
  }
  async deleteForm(formId: string) {
    await deleteDoc(doc(firestore, 'forms', formId));
  }
  async saveBudget(budget: AdsBudget) {
    const { id, ...rest } = budget;
    await setDoc(doc(firestore, 'ads_budgets', id), rest);
  }
  async createLead(lead: WebLead) {
    const { id, ...rest } = lead;
    await setDoc(doc(firestore, 'leads', id), rest);
  }
  async deleteLead(leadId: string) {
    await deleteDoc(doc(firestore, 'leads', leadId));
  }
  async deleteOrder(orderId: string) {
    await deleteDoc(doc(firestore, 'orders', orderId));
  }
  async updateOrder(orderId: string, updates: Partial<Order>) {
    await updateDoc(doc(firestore, 'orders', orderId), updates as any);
  }
  async deleteAbandonedCart(cartId: string) {
    await deleteDoc(doc(firestore, 'abandoned_carts', cartId));
  }
  async updateLead(leadId: string, updates: Partial<WebLead>) {
    await updateDoc(doc(firestore, 'leads', leadId), updates as any);
  }
  async updateLeadStatus(leadId: string, status: LeadStatus, notes?: string) {
    const updates: any = { status };
    if (notes) updates.notes = notes;
    await updateDoc(doc(firestore, 'leads', leadId), updates);
  }
  async saveWidget(widget: Widget) {
    const { id, ...rest } = widget;
    await setDoc(doc(firestore, 'widgets', id), rest);
  }
  async deleteWidget(widgetId: string) {
    await deleteDoc(doc(firestore, 'widgets', widgetId));
  }

  async saveRestock(restock: RestockRecord) {
    const { id, ...rest } = restock;
    await setDoc(doc(firestore, 'restocks', id), rest);
    // Update product stock
    const product = this.data.products.find(p => p.id === restock.productId);
    if (product) {
      await updateDoc(doc(firestore, 'products', product.id), {
        totalStock: product.totalStock + restock.quantity
      });
    }
  }

  async deleteRestock(restockId: string) {
    const restock = this.data.restocks.find(r => r.id === restockId);
    if (restock) {
      const product = this.data.products.find(p => p.id === restock.productId);
      if (product) {
        await updateDoc(doc(firestore, 'products', product.id), {
          totalStock: Math.max(0, product.totalStock - restock.quantity)
        });
      }
    }
    await deleteDoc(doc(firestore, 'restocks', restockId));
  }

  async saveWaybill(waybill: WaybillRecord) {
    const { id, ...rest } = waybill;
    await setDoc(doc(firestore, 'waybills', id), rest);
  }

  async deleteWaybill(waybillId: string) {
    await deleteDoc(doc(firestore, 'waybills', waybillId));
  }

  async deleteBudget(budgetId: string) {
    await deleteDoc(doc(firestore, 'ads_budgets', budgetId));
  }

  async saveState(state: StateConfig) {
    const { id, ...rest } = state;
    await setDoc(doc(firestore, 'states', id), rest);
  }

  async deleteState(stateId: string) {
    await deleteDoc(doc(firestore, 'states', stateId));
  }

  async getWidgetById(id: string): Promise<Widget | null> {
    try {
      const docRef = doc(firestore, 'widgets', id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as Widget;
      }
    } catch (e) {
      console.error("Error fetching widget:", e);
    }
    return null;
  }
}

export const db = new FirebaseDb();
